import { Suspense, lazy, useEffect } from 'react';
import { Link, Route, Routes, useLocation } from 'react-router-dom';
import { DESKTOP_NAV_QUERY, Header } from './components/Header';
import { Footer } from './components/Footer';
import { useSite } from './api/queries';
import { useMediaQuery } from './hooks/useMediaQuery';
import { HomePage } from './pages/HomePage';
import { ReservationsPage } from './pages/ReservationsPage';

/**
 * Route table for the public website.
 *
 * The home page and the reservation flow are in the main bundle because they carry the
 * two journeys most visitors take. Everything else is code-split so the first paint stays
 * light on mobile.
 */

const StoryPage = lazy(() => import('./pages/StoryPage').then((m) => ({ default: m.StoryPage })));
const MenuPage = lazy(() => import('./pages/MenuPage').then((m) => ({ default: m.MenuPage })));
const MenuItemPage = lazy(() => import('./pages/MenuItemPage').then((m) => ({ default: m.MenuItemPage })));
const ExperiencesPage = lazy(() => import('./pages/ExperiencesPage').then((m) => ({ default: m.ExperiencesPage })));
const EventsPage = lazy(() => import('./pages/EventsPage').then((m) => ({ default: m.EventsPage })));
const GalleryPage = lazy(() => import('./pages/GalleryPage').then((m) => ({ default: m.GalleryPage })));
const ConciergePage = lazy(() => import('./pages/ConciergePage').then((m) => ({ default: m.ConciergePage })));
const ConciergeTrackPage = lazy(() =>
  import('./pages/ConciergeTrackPage').then((m) => ({ default: m.ConciergeTrackPage })),
);
const LaundryPage = lazy(() => import('./pages/LaundryPage').then((m) => ({ default: m.LaundryPage })));
const LaundryTrackPage = lazy(() =>
  import('./pages/LaundryTrackPage').then((m) => ({ default: m.LaundryTrackPage })),
);
const ManageReservationPage = lazy(() =>
  import('./pages/ManageReservationPage').then((m) => ({ default: m.ManageReservationPage })),
);
const ContactPage = lazy(() => import('./pages/ContactPage').then((m) => ({ default: m.ContactPage })));
const FaqPage = lazy(() => import('./pages/FaqPage').then((m) => ({ default: m.FaqPage })));
const PolicyPage = lazy(() => import('./pages/PolicyPage').then((m) => ({ default: m.PolicyPage })));
const UnsubscribePage = lazy(() => import('./pages/UnsubscribePage').then((m) => ({ default: m.UnsubscribePage })));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage').then((m) => ({ default: m.NotFoundPage })));

/** Scrolls to the top on navigation, and moves focus so screen readers announce the new page. */
function RouteChangeEffects() {
  const { pathname } = useLocation();

  useEffect(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: 0, behavior: reduced ? 'auto' : 'smooth' });
    const main = document.getElementById('main-content');
    main?.focus({ preventScroll: true });
  }, [pathname]);

  return null;
}

function RouteFallback() {
  return (
    <div className="sz-page" style={{ minHeight: '60vh', display: 'grid', placeItems: 'center' }}>
      <div className="sz-spinner" role="status" aria-label="Loading page" />
    </div>
  );
}

export default function App() {
  const { data: site } = useSite();
  const location = useLocation();
  const isDesktop = useMediaQuery(DESKTOP_NAV_QUERY);

  // The design shows a sticky Reserve bar on mobile everywhere except home and the
  // reservation flow itself.
  const showStickyCta =
    !isDesktop && location.pathname !== '/' && !location.pathname.startsWith('/reservations');

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <a href="#main-content" className="sz-skip-link">
        Skip to content
      </a>

      <RouteChangeEffects />
      <Header />

      <main
        id="main-content"
        tabIndex={-1}
        style={{ flex: 1, outline: 'none', paddingBottom: showStickyCta ? 74 : 0 }}
      >
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/our-story" element={<StoryPage />} />
            <Route path="/menu/drinks" element={<MenuPage type="drinks" />} />
            <Route path="/menu/food" element={<MenuPage type="food" />} />
            <Route path="/menu/drinks/:slug" element={<MenuItemPage type="drinks" />} />
            <Route path="/menu/food/:slug" element={<MenuItemPage type="food" />} />
            <Route path="/experiences" element={<ExperiencesPage />} />
            <Route path="/events" element={<EventsPage />} />
            <Route path="/gallery" element={<GalleryPage />} />
            <Route path="/reservations" element={<ReservationsPage />} />
            <Route path="/manage-reservation" element={<ManageReservationPage />} />
            <Route path="/concierge" element={<ConciergePage />} />
            <Route path="/concierge/track" element={<ConciergeTrackPage />} />
            <Route path="/laundry" element={<LaundryPage />} />
            <Route path="/laundry/track" element={<LaundryTrackPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/faqs" element={<FaqPage />} />
            <Route path="/policies/:key" element={<PolicyPage />} />
            <Route path="/newsletter/unsubscribe" element={<UnsubscribePage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </main>

      <Footer site={site} />

      {showStickyCta && (
        <div className="sz-sticky-cta sz-no-print">
          <Link to="/reservations" className="sz-btn sz-btn--gold">
            Reserve a Table
          </Link>
        </div>
      )}
    </div>
  );
}
